import type { QueryClient } from '@tanstack/react-query';

// Custom localStorage persistence for React Query

const STORAGE_KEY = 'stock-vision-query-cache';
const CACHE_VERSION = '1.0';
const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

interface PersistedQueryCache {
  version: string;
  timestamp: number;
  queries: Record<
    string,
    {
      data: unknown;
      dataUpdateCount: number;
      dataUpdatedAt: number;
      error: null;
      errorUpdateCount: number;
      errorUpdatedAt: number;
      fetchFailureCount: number;
      fetchFailureReason: null;
      fetchMeta: null;
      isInvalidated: boolean;
      state: 'success' | 'error' | 'pending';
    }
  >;
}

export const persistQueryCache = (queryClient: QueryClient): void => {
  try {
    const cache = queryClient.getQueryCache();
    const queries: PersistedQueryCache['queries'] = {};

    cache.getAll().forEach(query => {
      const queryKey = JSON.stringify(query.queryKey);

      if (queryKey.includes('stockList') && query.state.status === 'success' && query.state.data) {
        queries[queryKey] = {
          data: query.state.data,
          dataUpdateCount: query.state.dataUpdateCount,
          dataUpdatedAt: query.state.dataUpdatedAt,
          error: null,
          errorUpdateCount: query.state.errorUpdateCount,
          errorUpdatedAt: query.state.errorUpdatedAt,
          fetchFailureCount: query.state.fetchFailureCount,
          fetchFailureReason: null,
          fetchMeta: null,
          isInvalidated: query.state.isInvalidated,
          state: query.state.status,
        };
      }
    });

    const persistedCache: PersistedQueryCache = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      queries,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedCache));

    if (import.meta.env.DEV) {
      console.log('Persisted to localStorage:', Object.keys(queries).length, 'queries');
    }
  } catch (error) {
    console.warn('Failed to persist to localStorage:', error);
  }
};

export const restoreQueryCache = (queryClient: QueryClient): void => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) return;

    const persistedCache: PersistedQueryCache = JSON.parse(cached);

    if (persistedCache.version !== CACHE_VERSION) {
      console.warn('Cache version mismatch, clearing cache');
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    const age = Date.now() - persistedCache.timestamp;
    if (age > MAX_AGE) {
      console.warn('Cache expired, clearing cache');
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    let restoredCount = 0;
    Object.entries(persistedCache.queries).forEach(([queryKeyStr, queryState]) => {
      try {
        const queryKey = JSON.parse(queryKeyStr);
        queryClient.setQueryData(queryKey, queryState.data);
        restoredCount++;
      } catch (error) {
        console.warn('Failed to restore query:', queryKeyStr, error);
      }
    });

    if (import.meta.env.DEV) {
      console.log('Restored from localStorage:', restoredCount, 'queries');
    }
  } catch (error) {
    console.warn('Failed to restore from localStorage:', error);
    localStorage.removeItem(STORAGE_KEY);
  }
};

export const clearPersistedCache = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    if (import.meta.env.DEV) {
      console.log('Cleared localStorage cache');
    }
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
};

export const getCacheStats = (): {
  exists: boolean;
  size: number;
  age: number;
  queriesCount: number;
  version: string;
} => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) {
      return {
        exists: false,
        size: 0,
        age: 0,
        queriesCount: 0,
        version: '',
      };
    }

    const persistedCache: PersistedQueryCache = JSON.parse(cached);
    return {
      exists: true,
      size: cached.length,
      age: Date.now() - persistedCache.timestamp,
      queriesCount: Object.keys(persistedCache.queries).length,
      version: persistedCache.version,
    };
  } catch (error) {
    console.warn('Failed to get cache stats:', error);
    return {
      exists: false,
      size: 0,
      age: 0,
      queriesCount: 0,
      version: '',
    };
  }
};
